import { EmployeePermissionsType, UserProvideMappingType } from './SharedEnums';
import { DigitalSignatureData } from './Paperwork/DigitalSignatureData';
import { DYWEmployeeInfo } from './DataYourWay/DYWEmployeeInfo';
import { IUserNotaryInformation } from './Notary/NotaryInformation';
import settings from 'settings';
import UserProviderMappings from '@core/classes/UserProviderMappings';
import util from '@core/services/util';

// Mirrors AccessEmployeeClass.cs
export class AccessEmployeeClass {
	id: string;

	addedByUser?: string;
	addedDateTime?: string | Date | moment.Moment;
	allowBrowser: boolean;
	allowDealActivation: boolean;
	allowRemoteDealMeetings: boolean;
	ignoreDepartmentRestriction: boolean;
	disableRecording: boolean;
	permissions: EmployeePermissionsType[];
	employeeAccess: EmployeeAccessClass;
	employeeCode?: string;
	employeeDefaults: EmployeeDefaultsClass;
	employeeDMSNumber: string;
	employeePasswordConfirm?: boolean;
	employeeEmail?: string;
	employeeFirstName?: string;
	employeeInitials?: string;
	employeeLastName?: string;
	employeeLogin?: string;
	employeePassword?: string;
	employeePhoneNumber?: string;
	employeeSignature?: string;
	employeeTheme?: string;
	employeeUser?: string;
	employeeWorkNumber?: string;
	externalEmployeeInfo: DYWEmployeeInfo = new DYWEmployeeInfo();
	failedLoginsAttempted: number;
	hasAccess: boolean;
	isArchived: boolean;
	isNotary?: boolean;
	userNotaryInformation: IUserNotaryInformation[];
	lastLoginAttempt?: string;
	locked: boolean;
	passwordExpireDate?: string | Date | moment.Moment;
	position?: string;
	positionCode: string;
	providerMappings: UserProviderMappings[];
	raffleTickets: number;
	signatureData?: DigitalSignatureData;
	initialsData?: DigitalSignatureData;
	verified: IVerifiedUserDevice[];

	get employeeName(): string {
		const useComma = this.employeeLastName && this.employeeFirstName;
		return `${this.employeeLastName ?? ''}${useComma ? ', ' : ''}${this.employeeFirstName ?? ''}`.trim();
	}

	constructor(init?: Partial<AccessEmployeeClass>) {
		this.id = init?.id ?? util.getDefaultGuid();
		this.employeeUser = init?.employeeUser ?? null;
		this.employeeCode = init?.employeeCode ?? null;
		this.employeeDMSNumber = init?.employeeDMSNumber ?? null;
		this.employeeLogin = init?.employeeLogin ?? null;
		this.employeePassword = init?.employeePassword ?? null;
		this.employeeFirstName = init?.employeeFirstName ?? null;
		this.employeeLastName = init?.employeeLastName ?? null;
		this.employeeEmail = init?.employeeEmail ?? null;
		this.employeeSignature = init?.employeeSignature ?? null;
		this.employeeInitials = init?.employeeInitials ?? null;
		this.employeePhoneNumber = init?.employeePhoneNumber ?? null;
		this.employeeWorkNumber = init?.employeeWorkNumber ?? null;
		this.employeeTheme = init?.employeeTheme ?? null;
		this.position = init?.position ?? null;
		this.positionCode = init?.positionCode ?? null;
		this.hasAccess = init?.hasAccess ?? false;
		this.allowDealActivation = init?.allowDealActivation ?? false;
		this.disableRecording = init?.disableRecording ?? false;
		this.passwordExpireDate = init?.passwordExpireDate ?? null;
		this.allowBrowser = init?.allowBrowser ?? false;
		this.allowRemoteDealMeetings = init?.allowRemoteDealMeetings ?? false;
		this.ignoreDepartmentRestriction = init?.ignoreDepartmentRestriction ?? false;
		this.permissions = init?.permissions ?? [];
		this.employeeAccess = new EmployeeAccessClass(init?.employeeAccess);
		this.employeeDefaults = new EmployeeDefaultsClass(init?.employeeDefaults);
		this.addedDateTime = init?.addedDateTime ?? null;
		this.addedByUser = init?.addedByUser ?? null;
		this.raffleTickets = init?.raffleTickets ?? 0;
		this.verified = init?.verified ?? [];
		this.failedLoginsAttempted = init?.failedLoginsAttempted ?? 0;
		this.lastLoginAttempt = init?.lastLoginAttempt ?? null;
		this.locked = init?.locked ?? false;
		this.isArchived = init?.isArchived ?? false;
		this.providerMappings = init?.providerMappings?.map(x => new UserProviderMappings(x)) ?? [];
		this.signatureData = init?.signatureData ? new DigitalSignatureData(init.signatureData) : null;
		this.initialsData = init?.initialsData ? new DigitalSignatureData(init.initialsData) : null;
		this.externalEmployeeInfo = new DYWEmployeeInfo(init?.externalEmployeeInfo);
		this.employeePasswordConfirm = init?.employeePasswordConfirm ?? false;
		this.isNotary = init?.isNotary ?? null;
		this.userNotaryInformation = init?.userNotaryInformation ?? [];
	}

	hasDDIAccess(): boolean {
		return (
			this.providerMappings.some(x => x.type == UserProvideMappingType.DDIPassword) && this.providerMappings.some(x => x.type == UserProvideMappingType.DDIUsername)
		);
	}

	get isAdmin(): boolean {
		return settings.permissions.isAdmin(this.positionCode);
	}

	/**
	 * Determines if a user has access to a given permission.
	 *
	 * @param permission The permission to check for access.
	 * @returns True if the user has access to the permission; otherwise, false.
	 */
	hasPermission(permission: EmployeePermissionsType): boolean {
		return this.permissions.includes(permission);
	}

	/**
	 * Determines if a user has access to a given permission.
	 *
	 * @param permission The permission to check for access.
	 * @returns True if the user has access to the permission; otherwise, false.
	 */
	hasAccessToPermission(permission: EmployeePermissionsType) {
		return this.isAdmin || this.hasPermission(permission);
	}
}

/** A bit flag representing all the permission claims a user has. */
export enum SaturnPermissions {
	// IMPORTANT: When updating this enum, make sure you match the naming conventions in EmployeePermissions.
	None = 0,

	SalesManager = 1 << 0,

	SalesPerson = 1 << 1,

	FIManager = 1 << 2,

	FIAdmin = 1 << 3,

	GeneralManager = 1 << 4,

	ServiceManager = 1 << 5,

	TitleClerk = 1 << 6,

	Director = 1 << 7,

	Accounting = 1 << 8,

	Admin = 1 << 9,

	UserManager = 1 << 10,

	ExceptionManager = 1 << 11,
}
export class EmployeeAccessClass {
	storeCodes: string[] = [];

	constructor(init?: Partial<EmployeeAccessClass>) {
		if (Array.isArray(init?.storeCodes)) {
			this.storeCodes = init?.storeCodes;
		}
	}
}

export class EmployeeDefaultsClass {
	constructor(partial?: Partial<EmployeeDefaultsClass>) {
		Object.assign(this, partial);
	}
}

export interface IVerifiedUserDevice {
	ipAddress: string;
	otp: string;
	timeOfCreation: string;
	timeOfConfirmation: string;
}

/*
EXAMPLE OF USER WITH FULL ACCESS:
{
    "employeeCode": "ADMIN",
    "employeeLogin": "ADMIN",
    "employeePassword": "--encrypted password--",
    "employeeFirstName": "",
    "employeeLastName": "Administrator",
    "employeeName": "Administrator",
    "employeeEmail": "",
    "position": "Administrator",
    "hasAccess": true,
    "positionCode": "ADMIN CODE",
    "permissions": [] // empty because they have admin level access
    "employeeAccess": {
        "storeCodes": [
            "MLCV", "MLCJ", "MLM", "MLK", "KCJ", "UD", "UM", "HCJ", "DM", "ACJ", "CCJ", "NEC", "JSM"
        ],
    },
    "employeeDefaults": {},
    "addedDateTime": "2020-03-23T16:01:19.9704703Z",
    "addedByUser": null
}

EXAMPLE OF BASIC USER:
{
    "employeeCode": "J1198",
    "employeeLogin": null,
    "employeePassword": null,
    "employeeFirstName": "RUEL",
    "employeeLastName": "FORBES",
    "employeeName": "FORBES, RUEL",
    "employeeEmail": null,
    "position": "SALES PERSON",
    "hasAccess": false,
    "positionCode": "SALES PERSON CODE",
    "permissions": [10, 20, 100, 105, 110],
    "employeeAccess": {
        "storeCodes": ["HCJ"],
    },
    "employeeDefaults": {},
    "addedDateTime": "2020-03-24T23:46:47.3370614Z",
    "addedByUser": null
}
*/
